import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { AlertCircle, Bell, CheckCircle2, Clock, FileText, Loader2, Receipt } from 'lucide-react';
import { db } from '../../utils/firebase';
import { subscribeChangeOrdersForDeal } from '../../utils/changeOrders';
import {
  buildPaymentScheduleBreakdown,
  changeOrderBillsImmediately,
  invoicedInstallmentLocks,
  isEffectiveProgressBilling,
  legacyDepositSatisfiedFromSalesOrder,
  type PaymentInstallmentId,
} from '../../utils/paymentSchedule';
import {
  invoiceClosesMilestone,
  invoiceIsVoid,
  milestonePackageChoices,
  type PartialInstallmentId,
} from '../../utils/partialInvoiceAllocation';
import { loadBillablePackagesForDeal } from '../../utils/partialInvoicePackages';
import { staffBillableTotalFromProposal } from '../../utils/proposalExclusiveEconomics';
import { getSignedCommercialListTotal } from '../../utils/proposalPublicGrandTotals';
import {
  triggerQuickBooksInvoiceForSalesOrder,
  type InstallmentId,
} from '../../utils/quickbooksInvoiceTrigger';
import { subscribeBillingReadyItemsForDeal } from '../../utils/readyToBillService';
import { BILLING_READY_STATUS_LABELS, type BillingReadyItem } from '../../utils/readyToBill';
import ProgressInvoiceConsolidateModal, {
  type ConsolidateCoRow,
} from './ProgressInvoiceConsolidateModal';
import PartialInvoicePackageModal, {
  type PartialInvoiceSelection,
} from './PartialInvoicePackageModal';
import type {
  ChangeOrder,
  ProposalBuilderData,
  SalesOrder,
  SalesOrderInstallmentInvoice,
} from '../../types';
import './PortalBillingSection.css';

interface PortalBillingSectionProps {
  dealId?: string | null;
  /** Fallback base contract total used before a sales order exists (pre-signing projection). */
  fallbackBaseTotal?: number;
}

type LoadedSalesOrder = SalesOrder & { id: string };

const fmt = (n: number) =>
  `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

function toDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  const maybe = v as { toDate?: () => Date };
  if (typeof maybe.toDate === 'function') return maybe.toDate();
  return null;
}

function formatDate(v: unknown): string {
  const d = toDate(v);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

function ReadyMark({ item }: { item: BillingReadyItem }) {
  const who = item.markedReadyByUserName?.trim();
  const when = formatDate(item.markedReadyAt);
  const title = [who, when].filter(Boolean).join(' · ');
  const tone = item.status === 'ready' ? 'ready' : item.status === 'paid' ? 'paid' : 'invoiced';
  return (
    <span className={`pbs-status pbs-status--${tone}`} title={title || undefined}>
      {item.status === 'ready' ? <Bell size={12} /> : item.status === 'paid' ? <CheckCircle2 size={13} /> : <FileText size={13} />}
      {BILLING_READY_STATUS_LABELS[item.status]}
    </span>
  );
}

function changeOrderIdFromRowId(rowId: string, changeOrders: ChangeOrder[]): string | null {
  if (!rowId.startsWith('co-')) return null;
  const suffix = rowId.slice(3);
  const match = changeOrders.find(c => c.id === suffix || String(c.number) === suffix);
  return match?.id ?? null;
}

const PortalBillingSection: React.FC<PortalBillingSectionProps> = ({ dealId, fallbackBaseTotal }) => {
  const [baseOrder, setBaseOrder] = useState<LoadedSalesOrder | null>(null);
  const [coSalesOrdersByCoId, setCoSalesOrdersByCoId] = useState<Record<string, LoadedSalesOrder>>({});
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [signedProposalBase, setSignedProposalBase] = useState<number | null>(null);
  const [projectedProposalBase, setProjectedProposalBase] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [billing, setBilling] = useState<InstallmentId | null>(null);
  const [consolidateModalOpen, setConsolidateModalOpen] = useState(false);
  const [consolidateModalError, setConsolidateModalError] = useState<string | null>(null);
  const [linkingLegacy, setLinkingLegacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readyItems, setReadyItems] = useState<BillingReadyItem[]>([]);
  const [pendingPartial, setPendingPartial] = useState<PartialInvoiceSelection | null>(null);
  const [packagePicker, setPackagePicker] = useState<{
    installmentId: InstallmentId;
    structures: Awaited<ReturnType<typeof loadBillablePackagesForDeal>>['structures'];
    packages: ReturnType<typeof milestonePackageChoices>['packages'];
  } | null>(null);

  // Live sales orders for this deal (base + change-order SOs).
  useEffect(() => {
    setBaseOrder(null);
    setCoSalesOrdersByCoId({});
    setLoaded(false);
    if (!dealId) {
      setLoaded(true);
      return;
    }
    const q = query(collection(db, 'salesOrders'), where('dealId', '==', dealId));
    const unsub = onSnapshot(
      q,
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...(d.data() as SalesOrder) }));
        const base = all.find(o => !o.isChangeOrder);
        const coMap: Record<string, LoadedSalesOrder> = {};
        for (const o of all) {
          if (o.isChangeOrder && o.changeOrderId) coMap[o.changeOrderId] = o;
        }
        setBaseOrder(base ?? null);
        setCoSalesOrdersByCoId(coMap);
        setLoaded(true);
      },
      err => {
        console.warn('[PortalBillingSection] sales order subscription error', err);
        setLoaded(true);
      },
    );
    return () => unsub();
  }, [dealId]);

  // Signed commercial total when signed; otherwise the default Choose one pick.
  useEffect(() => {
    setSignedProposalBase(null);
    setProjectedProposalBase(null);
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'proposalBuilders', dealId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data() as ProposalBuilderData;
        setSignedProposalBase(getSignedCommercialListTotal(data));
        setProjectedProposalBase(staffBillableTotalFromProposal(data));
      } catch (err) {
        console.warn('[PortalBillingSection] proposal load failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  useEffect(() => {
    setReadyItems([]);
    if (!dealId) return;
    return subscribeBillingReadyItemsForDeal(
      dealId,
      setReadyItems,
      (err) => console.warn('[PortalBillingSection] ready-to-bill subscription error', err),
    );
  }, [dealId]);

  // Live change orders (for folded contract-terms adjustments).
  useEffect(() => {
    setChangeOrders([]);
    if (!dealId) return;
    const unsub = subscribeChangeOrdersForDeal(dealId, setChangeOrders);
    return () => unsub();
  }, [dealId]);

  const readyByInstallment = useMemo(() => {
    const map = new Map<string, BillingReadyItem>();
    for (const item of readyItems) {
      if (!item.installmentId || item.status === 'dismissed') continue;
      const prev = map.get(item.installmentId);
      if (!prev || item.markedReadyAt > prev.markedReadyAt) map.set(item.installmentId, item);
    }
    return map;
  }, [readyItems]);

  const installmentInvoices: SalesOrderInstallmentInvoice[] = useMemo(
    () => (baseOrder?.installmentInvoices as SalesOrderInstallmentInvoice[]) || [],
    [baseOrder],
  );

  const legacyDeposit = legacyDepositSatisfiedFromSalesOrder(baseOrder);

  const baseTotal =
    signedProposalBase ?? baseOrder?.grandTotal ?? projectedProposalBase ?? fallbackBaseTotal ?? 0;

  // Deposit stays passed. An installment invoice locks only change orders
  // approved after that invoice, so a later credit cannot rewrite a paid bill.
  const breakdown = useMemo(
    () => buildPaymentScheduleBreakdown(baseTotal, changeOrders, {
      invoicedInstallments: invoicedInstallmentLocks(installmentInvoices),
    }),
    [baseTotal, changeOrders, installmentInvoices],
  );

  const isSigned = !!baseOrder;
  const isProgress = isEffectiveProgressBilling(baseOrder);
  const isTrueLegacyFull =
    isSigned && baseOrder?.billingStyle !== 'progress' && !!baseOrder?.quickbooksInvoiceId && !legacyDeposit;

  const milestoneClosed = useCallback((id: string) => (
    installmentInvoices.some((entry) => entry.installmentId === id && invoiceClosesMilestone(entry))
    || (id === 'deposit' && legacyDeposit)
  ), [installmentInvoices, legacyDeposit]);

  const bundleableImmediateCos = useMemo((): ConsolidateCoRow[] => {
    return changeOrders
      .filter(co => {
        if (co.status !== 'approved' || !changeOrderBillsImmediately(co) || !co.id) return false;
        const coSo = coSalesOrdersByCoId[co.id];
        if (!coSo) return false;
        if ((coSo.bundledOnInstallmentInvoice)) {
          return false;
        }
        return true;
      })
      .map(co => ({
        changeOrder: co,
        salesOrder: co.id ? coSalesOrdersByCoId[co.id] : undefined,
        amount: Math.round(Number(co.deltaAmount) || 0),
      }));
  }, [changeOrders, coSalesOrdersByCoId]);

  const preShipmentRow = useMemo(
    () => breakdown.rows.find(r => r.kind === 'installment' && r.id === 'pre_shipment'),
    [breakdown.rows],
  );

  const createInstallmentInvoice = useCallback(
    async (
      installmentId: InstallmentId,
      options?: { consolidateChangeOrderIds?: string[]; partial?: PartialInvoiceSelection | null },
    ) => {
      if (!baseOrder?.id) return;
      setBilling(installmentId);
      setConsolidateModalError(null);
      setError(null);
      try {
        const consolidateIds = options?.consolidateChangeOrderIds;
        const partial = options?.partial;
        const res = await triggerQuickBooksInvoiceForSalesOrder(baseOrder.id, installmentId, {
          consolidateImmediateChangeOrders:
            installmentId === 'pre_shipment' && !!consolidateIds && consolidateIds.length > 0,
          consolidateChangeOrderIds: consolidateIds,
          structureIds: partial?.structureIds,
          structureGroupKeys: partial?.structureGroupKeys,
          structureGroupLabels: partial?.structureGroupLabels,
          omittedPackageLabels: partial?.omittedPackageLabels,
          structureGroups: partial?.structureGroups,
          foldChangeOrderRefs: partial?.foldChangeOrderRefs,
        });
        if (!res.ok) {
          const msg = res.error || 'Failed to create invoice';
          if (consolidateModalOpen) setConsolidateModalError(msg);
          else setError(msg);
          return;
        }
        setConsolidateModalOpen(false);
        setPackagePicker(null);
        setPendingPartial(null);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create invoice';
        if (consolidateModalOpen) setConsolidateModalError(msg);
        else setError(msg);
      } finally {
        setBilling(null);
      }
    },
    [baseOrder?.id, consolidateModalOpen],
  );

  const continueInvoice = useCallback(async (
    installmentId: InstallmentId,
    partial: PartialInvoiceSelection | null,
  ) => {
    if (installmentId === 'pre_shipment' && bundleableImmediateCos.length > 0) {
      setPendingPartial(partial);
      setConsolidateModalError(null);
      setConsolidateModalOpen(true);
      return;
    }
    await createInstallmentInvoice(installmentId, { partial });
  }, [bundleableImmediateCos.length, createInstallmentInvoice]);

  const handleBill = useCallback(
    async (installmentId: InstallmentId) => {
      if (!baseOrder || installmentId === 'deposit') {
        await createInstallmentInvoice(installmentId);
        return;
      }
      setError(null);
      setBilling(installmentId);
      try {
        const loaded = await loadBillablePackagesForDeal(dealId || '', baseOrder.lineItems);
        const choice = milestonePackageChoices({
          installmentId: installmentId as PartialInstallmentId,
          packages: loaded.packages,
          structures: loaded.structures,
          invoices: installmentInvoices,
          changeOrders: breakdown.rows.find(
            (row) => row.kind === 'installment' && row.id === installmentId,
          )?.coAdjustments,
        });
        if (choice.mode === 'pick') {
          setPackagePicker({
            installmentId,
            structures: loaded.structures,
            packages: choice.packages,
          });
          return;
        }
        if (
          choice.mode === 'full'
          && choice.packages.length > 0
          && choice.packages.every((row) => row.disabledReason)
        ) {
          setError(choice.packages.find((row) => row.disabledReason)?.disabledReason
            || 'Bill the pre-shipment invoice for this package first.');
          return;
        }
        if (choice.mode === 'single') {
          const pkg = choice.packages.find((row) => row.structureIds.join('|') === choice.structureIds.join('|'))
            || choice.packages.find((row) => !row.disabledReason);
          const omitted = choice.packages
            .filter((row) => row.key !== pkg?.key && !row.disabledReason)
            .map((row) => row.label);
          await continueInvoice(installmentId, {
            structureIds: choice.structureIds,
            structureGroupKeys: pkg ? [pkg.key] : [],
            structureGroupLabels: pkg ? [pkg.label] : [],
            omittedPackageLabels: omitted,
            structureGroups: pkg ? [{ key: pkg.key, label: pkg.label, structureIds: pkg.structureIds }] : [],
          });
          return;
        }
        await continueInvoice(installmentId, null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load structure groups');
      } finally {
        setBilling(null);
      }
    },
    [baseOrder, breakdown.rows, continueInvoice, createInstallmentInvoice, dealId, installmentInvoices],
  );

  const handleConsolidateConfirm = useCallback(
    async (selectedChangeOrderIds: string[]) => {
      await createInstallmentInvoice('pre_shipment', {
        consolidateChangeOrderIds: selectedChangeOrderIds,
        partial: pendingPartial,
      });
    },
    [createInstallmentInvoice, pendingPartial],
  );

  const handleLinkLegacyDeposit = useCallback(async () => {
    if (!baseOrder?.id || !baseOrder.quickbooksInvoiceId || !legacyDeposit) return;
    const depositRow = breakdown.rows.find(r => r.kind === 'installment' && r.id === 'deposit');
    setLinkingLegacy(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'salesOrders', baseOrder.id), {
        billingStyle: 'progress',
        installmentInvoices: [
          ...(baseOrder.installmentInvoices || []),
          {
            installmentId: 'deposit' as PaymentInstallmentId,
            quickbooksInvoiceId: baseOrder.quickbooksInvoiceId,
            quickbooksInvoiceNum: baseOrder.quickbooksInvoiceNum || '',
            amount: depositRow?.amount ?? Math.round(baseTotal * 0.5),
            status: 'paid',
            createdAt: baseOrder.quickbooksInvoiceCreatedAt || new Date(),
            paidAt: baseOrder.quickbooksInvoiceCreatedAt || new Date(),
          },
        ],
        installmentInvoiceIds: baseOrder.quickbooksInvoiceId
          ? Array.from(new Set([...(baseOrder.installmentInvoiceIds || []), baseOrder.quickbooksInvoiceId]))
          : baseOrder.installmentInvoiceIds,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link legacy deposit');
    } finally {
      setLinkingLegacy(false);
    }
  }, [baseOrder, legacyDeposit, breakdown.rows, baseTotal]);

  if (!dealId) return null;

  const installmentRows = breakdown.rows.filter(r => r.kind === 'installment');
  const changeOrderRows = breakdown.rows.filter(r => r.kind === 'change_order');

  return (
    <section className="pbs-card" data-tour="portal-billing">
      <header className="pbs-head">
        <div className="pbs-head-title">
          <Receipt size={16} />
          <h3>Billing</h3>
        </div>
        <span className="pbs-head-total">{fmt(breakdown.currentContractValue)}</span>
      </header>

      {!loaded ? (
        <div className="pbs-empty">Loading…</div>
      ) : (
        <>
          {isTrueLegacyFull && (
            <div className="pbs-note pbs-note--info">
              Billed in full (legacy)
              {baseOrder?.quickbooksInvoiceNum ? ` — Invoice #${baseOrder.quickbooksInvoiceNum}` : ''}.
            </div>
          )}
          {legacyDeposit && (
            <div className="pbs-note pbs-note--info">
              Deposit paid via legacy invoice
              {baseOrder?.quickbooksInvoiceNum ? ` #${baseOrder.quickbooksInvoiceNum}` : ''}. Remaining
              installments follow the progress billing schedule.
              {!installmentInvoices.some(e => e.installmentId === 'deposit') && (
                <button
                  type="button"
                  className="pbs-link-btn"
                  disabled={linkingLegacy}
                  onClick={handleLinkLegacyDeposit}
                >
                  {linkingLegacy ? 'Linking…' : 'Link as deposit record'}
                </button>
              )}
            </div>
          )}
          {!isSigned && (
            <div className="pbs-note">Projected schedule — awaiting signature.</div>
          )}

          <ul className="pbs-rows">
            {installmentRows.map(row => {
              const entries = installmentInvoices.filter(
                (entry) => entry.installmentId === row.id && !invoiceIsVoid(entry.status),
              );
              const entry = entries.find((item) => invoiceClosesMilestone(item)) || entries[0];
              const partials = entries.filter((item) => !!item.partialId || (item.structureIds?.length ?? 0) > 0);
              const invoicedTotal = entries.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
              const showInvoicedTotal = invoicedTotal > 0 && invoicedTotal < row.amount;
              const closed = milestoneClosed(row.id);
              const isLegacyPaidDeposit = legacyDeposit && row.id === 'deposit' && entries.length === 0;
              const depositReady = milestoneClosed('deposit');
              const preHasInvoice = installmentInvoices.some(
                (item) => item.installmentId === 'pre_shipment' && item.status !== 'voided',
              );
              const canBill = !isTrueLegacyFull && isProgress && isSigned && !closed && (
                row.id === 'deposit'
                  ? !depositReady
                  : row.id === 'pre_shipment'
                    ? depositReady
                    : depositReady && preHasInvoice
              );
              const readyItem = readyByInstallment.get(row.id);
              const status = closed ? (entry?.status || (isLegacyPaidDeposit ? 'paid' : 'open')) : undefined;
              const isPaid = status === 'paid' || isLegacyPaidDeposit;
              const isPartialPay = status === 'partial';

              let statusEl: React.ReactNode;
              if (closed && isPaid) {
                statusEl = (
                  <span className="pbs-status pbs-status--paid">
                    <CheckCircle2 size={13} /> Paid
                    {isLegacyPaidDeposit && baseOrder?.quickbooksInvoiceNum
                      ? ` (legacy #${baseOrder.quickbooksInvoiceNum})`
                      : entry?.paidAt
                        ? ` · ${formatDate(entry.paidAt)}`
                        : ''}
                  </span>
                );
              } else if (closed && isPartialPay) {
                statusEl = <span className="pbs-status pbs-status--partial"><Clock size={13} /> Partially paid</span>;
              } else if (closed && entry) {
                statusEl = (
                  <span className="pbs-status pbs-status--invoiced">
                    <FileText size={13} /> Invoiced{entry.quickbooksInvoiceNum ? ` #${entry.quickbooksInvoiceNum}` : ''}
                  </span>
                );
              } else if (readyItem?.status === 'paid' && partials.length === 0) {
                statusEl = <ReadyMark item={readyItem} />;
              } else if (canBill) {
                statusEl = (
                  <>
                    {readyItem && partials.length === 0 && <ReadyMark item={readyItem} />}
                    <button
                      type="button"
                      className="pbs-bill-btn"
                      disabled={billing !== null}
                      onClick={() => handleBill(row.id as InstallmentId)}
                    >
                      {billing === row.id ? <Loader2 size={13} className="pbs-spin" /> : <Receipt size={13} />}
                      {billing === row.id ? 'Creating…' : row.id === 'pre_shipment' && bundleableImmediateCos.length > 0
                        ? 'Review & create invoice'
                        : partials.length > 0
                          ? 'Invoice remaining'
                          : 'Create invoice'}
                    </button>
                  </>
                );
              } else if (readyItem && partials.length === 0) {
                statusEl = <ReadyMark item={readyItem} />;
              } else if (!isSigned) {
                statusEl = <span className="pbs-status pbs-status--pending">Awaiting signature</span>;
              } else {
                statusEl = <span className="pbs-status pbs-status--pending">Not yet billed</span>;
              }

              return (
                <li key={row.id} className="pbs-row">
                  <div className="pbs-row-info">
                    <span className="pbs-row-label">{row.label}</span>
                    <span className="pbs-row-detail">{row.detail}</span>
                    {partials.map((partial) => (
                      <span key={partial.partialId || partial.quickbooksInvoiceId} className="pbs-row-detail">
                        {(partial.structureGroupLabels || []).join(', ') || 'Partial'}
                        {' · '}
                        {fmt(partial.amount)}
                        {partial.quickbooksInvoiceNum ? ` · #${partial.quickbooksInvoiceNum}` : ''}
                        {partial.status === 'paid' ? ' · Paid' : partial.status === 'partial' ? ' · Partially paid' : ' · Invoiced'}
                      </span>
                    ))}
                    {row.coAdjustments.length > 0 && (
                      <span className="pbs-row-adj">
                        Base {fmt(row.baseAmount)}
                        {row.coAdjustments.map((adj, i) => (
                          <span key={`${adj.reference}-${i}`} className={adj.amount < 0 ? 'pbs-adj-credit' : 'pbs-adj-add'}>
                            {' '}
                            {adj.amount >= 0 ? '+' : '−'}{fmt(Math.abs(adj.amount))} ({adj.reference})
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                  <div className="pbs-row-right">
                    <span className="pbs-row-figures">
                      {showInvoicedTotal && (
                        <span className="pbs-row-billed">
                          <span className="pbs-row-billed-amount">{fmt(invoicedTotal)}</span>
                          <span className="pbs-row-billed-label">invoiced</span>
                        </span>
                      )}
                      <span className="pbs-row-amount">{fmt(row.amount)}</span>
                    </span>
                    {statusEl}
                  </div>
                </li>
              );
            })}

            {changeOrderRows.map(row => {
              const coId = changeOrderIdFromRowId(row.id, changeOrders);
              const coSo = coId ? coSalesOrdersByCoId[coId] : undefined;
              const coInvoiced = !!coSo?.quickbooksInvoiceId;
              const coReady = coId ? readyByInstallment.get(`co-${coId}`) : undefined;
              const bundledOn = coSo?.bundledOnInstallmentInvoice;
              const parentInvoice = bundledOn
                ? installmentInvoices.find(e => e.installmentId === bundledOn && e.status !== 'voided')
                : undefined;
              const bundledPaid = parentInvoice?.status === 'paid';
              const bundledPartial = parentInvoice?.status === 'partial';

              let coStatus: React.ReactNode;
              if (bundledPaid) {
                coStatus = (
                  <span className="pbs-status pbs-status--paid">
                    <CheckCircle2 size={13} /> Paid
                    {parentInvoice?.paidAt ? ` · ${formatDate(parentInvoice.paidAt)}` : ''}
                  </span>
                );
              } else if (bundledPartial) {
                coStatus = <span className="pbs-status pbs-status--partial"><Clock size={13} /> Partially paid</span>;
              } else if (coReady && !coInvoiced && !bundledOn) {
                coStatus = <ReadyMark item={coReady} />;
              } else {
                coStatus = (
                  <span className={`pbs-status ${coInvoiced || bundledOn ? 'pbs-status--invoiced' : 'pbs-status--pending'}`}>
                    {bundledOn
                      ? 'On progress invoice'
                      : coInvoiced
                        ? `Invoiced${coSo?.quickbooksInvoiceNum ? ` #${coSo.quickbooksInvoiceNum}` : ''}`
                        : !milestoneClosed('pre_shipment')
                          ? 'On next invoice'
                          : 'Due separately'}
                  </span>
                );
              }

              return (
                <li key={row.id} className="pbs-row pbs-row--co">
                  <div className="pbs-row-info">
                    <span className="pbs-row-label">{row.label}</span>
                    <span className="pbs-row-detail">{row.detail}</span>
                  </div>
                  <div className="pbs-row-right">
                    <span className="pbs-row-amount">{fmt(row.amount)}</span>
                    {coStatus}
                  </div>
                </li>
              );
            })}
          </ul>

          {breakdown.refundDue > 0 && (
            <div className="pbs-note pbs-note--warn">
              Credits exceed the remaining unpaid balance — refund due to client: {fmt(breakdown.refundDue)}
            </div>
          )}

          {error && (
            <div className="pbs-note pbs-note--error">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          {packagePicker && (() => {
            const pickerRow = breakdown.rows.find((row) => row.kind === 'installment' && row.id === packagePicker.installmentId);
            if (!pickerRow) return null;
            return (
              <PartialInvoicePackageModal
                open
                installmentId={packagePicker.installmentId}
                milestoneLabel={pickerRow.label}
                milestoneDetail={pickerRow.detail}
                baseAmount={pickerRow.baseAmount}
                milestoneAmount={pickerRow.amount}
                coAdjustments={pickerRow.coAdjustments}
                packages={packagePicker.packages}
                structures={packagePicker.structures}
                invoices={installmentInvoices}
                onClose={() => setPackagePicker(null)}
                onConfirm={(selection) => {
                  const installmentId = packagePicker.installmentId;
                  setPackagePicker(null);
                  void continueInvoice(
                    installmentId,
                    selection.structureIds.length || Array.isArray(selection.foldChangeOrderRefs)
                      ? selection
                      : null,
                  );
                }}
              />
            );
          })()}

          <ProgressInvoiceConsolidateModal
            open={consolidateModalOpen}
            onClose={() => {
              if (billing) return;
              setConsolidateModalOpen(false);
              setConsolidateModalError(null);
            }}
            onConfirm={handleConsolidateConfirm}
            installmentLabel={preShipmentRow?.label || '40%'}
            installmentDetail={preShipmentRow?.detail || 'Due prior to shipment'}
            installmentAmount={preShipmentRow?.amount ?? 0}
            coAdjustments={(preShipmentRow?.coAdjustments || []).map(adj => ({
              reference: adj.reference,
              amount: adj.amount,
              title: adj.title,
            }))}
            changeOrders={bundleableImmediateCos}
            submitting={billing === 'pre_shipment'}
            error={consolidateModalError}
          />
        </>
      )}
    </section>
  );
};

export default PortalBillingSection;
